import { useQuery, useMutation, gql } from '@apollo/client';
import { useMemo } from 'react';

// ==================== Types ====================

export interface SystemModule {
  id: string;
  key: string;
  name: string;
  description: string | null;
  icon: string | null;
  path: string;
  category: string;
  enabled: boolean;
  isCore: boolean;
  sortOrder: number;
  lastChangedBy: string | null;
  lastChangedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ModuleCategory {
  key: string;
  name: string;
  modules: SystemModule[];
}

// ==================== GraphQL Operations ====================

const GET_ALL_MODULES = gql`
  query GetAllSystemModules {
    getAllSystemModules {
      id
      key
      name
      description
      icon
      path
      category
      enabled
      isCore
      sortOrder
      lastChangedBy
      lastChangedAt
      createdAt
      updatedAt
    }
  }
`;

const GET_ENABLED_MODULES = gql`
  query GetEnabledModules {
    getEnabledModules {
      id
      key
      name
      description
      icon
      path
      category
      enabled
      isCore
      sortOrder
    }
  }
`;

const GET_MODULES_BY_CATEGORY = gql`
  query GetModulesByCategory {
    getModulesByCategory {
      key
      name
      modules {
        id
        key
        name
        description
        icon
        path
        category
        enabled
        isCore
        sortOrder
        lastChangedBy
        lastChangedAt
      }
    }
  }
`;

const GET_ENABLED_PATHS = gql`
  query GetEnabledPaths {
    getEnabledPaths
  }
`;

const IS_MODULE_ENABLED = gql`
  query IsModuleEnabled($key: String!) {
    isModuleEnabled(key: $key)
  }
`;

const TOGGLE_MODULE = gql`
  mutation ToggleSystemModule($input: ToggleModuleInput!) {
    toggleSystemModule(input: $input) {
      id
      key
      name
      enabled
      isCore
      lastChangedBy
      lastChangedAt
    }
  }
`;

const INITIALIZE_MODULES = gql`
  mutation InitializeSystemModules {
    initializeSystemModules
  }
`;

// ==================== Hooks ====================

/**
 * Hook para obtener todos los módulos del sistema
 */
export function useAllSystemModules() {
  const { data, loading, error, refetch } = useQuery<{ getAllSystemModules: SystemModule[] }>(
    GET_ALL_MODULES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    modules: data?.getAllSystemModules || [],
    loading,
    error,
    refetch,
  };
}

// Rutas por defecto que siempre deben estar visibles mientras se cargan los módulos
const DEFAULT_ENABLED_PATHS = [
  '/',
  '/configuracion',
  '/autorizacion',
  '/monto-diario',
  '/monto-impuestos',
  '/horarios',
  '/ajustar-facturas',
  '/generar-facturas',
  '/control-predictivo',
  '/logs',
  '/usuarios',
];

/**
 * Hook para obtener solo los módulos habilitados
 * Mientras se carga, retorna módulos ficticios para evitar que el menú parpadee vacío
 */
export function useEnabledModules() {
  const { data, loading, error, refetch } = useQuery<{ getEnabledModules: SystemModule[] }>(
    GET_ENABLED_MODULES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  // Crear módulos por defecto para mostrar mientras se carga
  const defaultModules = useMemo(() =>
    DEFAULT_ENABLED_PATHS.map((path, index) => ({
      id: `default-${index}`,
      key: path.replace('/', '') || 'dashboard',
      name: path,
      description: null,
      icon: null,
      path,
      category: 'default',
      enabled: true,
      isCore: false,
      sortOrder: index,
      lastChangedBy: null,
      lastChangedAt: null,
      createdAt: new Date(),
      updatedAt: new Date(),
    })) as SystemModule[]
  , []);

  // Si hay datos del servidor, usarlos; si no, usar los defaults mientras carga
  const modules = data?.getEnabledModules?.length
    ? data.getEnabledModules
    : (loading ? defaultModules : []);

  return {
    modules,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para obtener módulos agrupados por categoría
 */
export function useModulesByCategory() {
  const { data, loading, error, refetch } = useQuery<{ getModulesByCategory: ModuleCategory[] }>(
    GET_MODULES_BY_CATEGORY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    categories: data?.getModulesByCategory || [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para obtener rutas habilitadas (para navegación)
 */
export function useEnabledPaths() {
  const { data, loading, error, refetch } = useQuery<{ getEnabledPaths: string[] }>(
    GET_ENABLED_PATHS,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    paths: data?.getEnabledPaths || [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para verificar si un módulo específico está habilitado
 */
export function useIsModuleEnabled(key: string) {
  const { data, loading, error, refetch } = useQuery<{ isModuleEnabled: boolean }>(
    IS_MODULE_ENABLED,
    {
      variables: { key },
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    isEnabled: data?.isModuleEnabled ?? false,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook para habilitar/deshabilitar un módulo
 */
export function useToggleModule() {
  const [toggleMutation, { loading, error }] = useMutation(TOGGLE_MODULE, {
    refetchQueries: [
      { query: GET_ALL_MODULES },
      { query: GET_ENABLED_MODULES },
      { query: GET_MODULES_BY_CATEGORY },
      { query: GET_ENABLED_PATHS },
    ],
  });

  const toggle = async (moduleKey: string, enabled: boolean, changedBy: string = 'admin') => {
    const result = await toggleMutation({
      variables: {
        input: {
          moduleKey,
          enabled,
          changedBy,
        },
      },
    });
    return result.data?.toggleSystemModule;
  };

  return {
    toggle,
    loading,
    error,
  };
}

/**
 * Hook para inicializar módulos (crear los predeterminados si no existen)
 */
export function useInitializeModules() {
  const [initMutation, { loading, error }] = useMutation(INITIALIZE_MODULES, {
    refetchQueries: [
      { query: GET_ALL_MODULES },
      { query: GET_MODULES_BY_CATEGORY },
    ],
  });

  const initialize = async () => {
    const result = await initMutation();
    return result.data?.initializeSystemModules;
  };

  return {
    initialize,
    loading,
    error,
  };
}
